import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./Home.css";
import MapboxMap from "../components/MapboxMap";
import Header from "../components/Header";
import RewardPopup from "../components/RewardPopup";
import HintModal from "../components/HintModal";
import GamePopup from "../components/GamePopup";
import BoostScorePopup from "../components/BoostScorePopup";
import JigsawTrayPuzzle from "../components/JigsawTrayPuzzle";
import FlipCardsGame from "../components/FlipCardsGame";
import MarathonQuizGame from "../components/MarathonQuizGame";
import toast from "react-hot-toast";
import { useUser } from "../context/UserContext";
import { useDrawer } from "../context/DrawerContext";
import { getTreasureData } from "../utils/treasureDataSelector";
import { normalizeTreasureId } from "../utils/dataValidation";
import { findTreasureByName } from "../utils/treasureNameMatcher";
import treasureImage from "../assets/images/optimized/treasureIcon.png";
import { getCachedDistance } from "../services/firebase";

const Home = () => {
  const location = useLocation();
  const { userData, updateUserData } = useUser();

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    return getCachedDistance([lon1, lat1], [lon2, lat2]);
  };

  const isValidSydneyCoordinate = (longitude, latitude) => {
    const minLng = 150.5;
    const maxLng = 151.5;
    const minLat = -34.5;
    const maxLat = -33.0;

    return (
      longitude >= minLng &&
      longitude <= maxLng &&
      latitude >= minLat &&
      latitude <= maxLat
    );
  };

  const DEFAULT_SYDNEY_COORDS = [151.2093, -33.8688];

  const [center, setCenter] = useState([151.2093, -33.8688]);

  const [currentTreasureData, setCurrentTreasureData] = useState([]);

  const [treasures, setTreasures] = useState([]);
  const [userPosition, setUserPosition] = useState(DEFAULT_SYDNEY_COORDS);
  const [activeView, setActiveView] = useState("map");
  const [selectedTreasure, setSelectedTreasure] = useState(null);
  const [isHintModalOpen, setIsHintModalOpen] = useState(false);
  const [isRewardPopupOpen, setIsRewardPopupOpen] = useState(false);
  const [rewardData, setRewardData] = useState(null);
  const [showBoostPopup, setShowBoostPopup] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [showHintBalloon, setShowHintBalloon] = useState(false);
  const [isSavingTreasure, setIsSavingTreasure] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasShownLocationWarning, setHasShownLocationWarning] = useState(() => {
    // Check if we've already shown the location warning in this session
    return localStorage.getItem("hasShownLocationWarning") === "true";
  });
  const [isLocating, setIsLocating] = useState(true);
  const permissionInitRef = useRef(false);
  const locationWatchId = useRef(null);
  const { isDrawerOpen } = useDrawer();

  // Function to determine if user is near the specific Sydney Marathon area
  const isNearSydneyMarathonArea = (longitude, latitude) => {
    // Specific coordinates for Sydney Marathon area
    const marathonCenterLng = 151.20811549611122;
    const marathonCenterLat = -33.859119658821044;
    const marathonRadius = 0.0005; // Approximately 50m radius for precise area

    const distance = Math.sqrt(
      Math.pow(longitude - marathonCenterLng, 2) +
        Math.pow(latitude - marathonCenterLat, 2)
    );

    return distance <= marathonRadius;
  };

  // Function to reset location warning (for testing purposes)
  const resetLocationWarning = () => {
    setHasShownLocationWarning(false);
    localStorage.removeItem("hasShownLocationWarning");
  };

  // Function to get the appropriate iframe URL based on location
  const getIframeUrl = () => {
    if (
      userPosition &&
      isValidSydneyCoordinate(userPosition[0], userPosition[1])
    ) {
      if (isNearSydneyMarathonArea(userPosition[0], userPosition[1])) {
        return "https://nxtinteractive.8thwall.app/sydney-marathon2/";
      }
    }
    // Default URL for users outside the specific marathon area
    return "https://nxtinteractive.8thwall.app/sydneymarathaon/";
  };

  const resetMapView = useCallback(() => {
    if (
      userPosition &&
      isValidSydneyCoordinate(userPosition[0], userPosition[1]) &&
      userPosition !== DEFAULT_SYDNEY_COORDS
    ) {
      setCenter(userPosition);
    } else {
      setCenter(DEFAULT_SYDNEY_COORDS);
    }
  }, [userPosition]);

  // Initial location detection when component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          if (isValidSydneyCoordinate(longitude, latitude)) {
            setUserPosition([longitude, latitude]);
            setCenter([longitude, latitude]); // Center map on user location
          }
          setIsLocating(false);
        },
        (error) => {
          console.log("Initial location access denied, using default");
          setIsLocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // Cache for 5 minutes
        }
      );
    } else {
      setIsLocating(false);
    }
  }, []); // Run only once on mount

  // Refresh location when switching back to map view
  useEffect(() => {
    if (activeView === "map" && navigator.geolocation) {
      // Small delay to ensure view transition is complete
      const timer = setTimeout(() => {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { longitude, latitude } = position.coords;
            if (isValidSydneyCoordinate(longitude, latitude)) {
              setUserPosition([longitude, latitude]);
              setCenter([longitude, latitude]); // Center map on user's current location
              console.log("📍 [MAP VIEW] Location refreshed via useEffect:", [
                longitude,
                latitude,
              ]);
            }
            setIsLocating(false);
          },
          (error) => {
            console.log(
              "📍 [MAP VIEW] Location refresh failed via useEffect:",
              error.message
            );
            setIsLocating(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0, // Don't use cached position
          }
        );
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [activeView]); // Run when activeView changes

  useEffect(() => {
    if (selectedTreasure) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { longitude, latitude } = position.coords;

            if (isValidSydneyCoordinate(longitude, latitude)) {
              setUserPosition([longitude, latitude]);
              setHasUserInteracted(false);
            } else {
              setUserPosition(DEFAULT_SYDNEY_COORDS);
              setHasUserInteracted(false);
              // Only show warning once to avoid spam
              if (!hasShownLocationWarning) {
                setHasShownLocationWarning(true);
                localStorage.setItem("hasShownLocationWarning", "true");
                toast(
                  "Location outside Sydney area. Using default Sydney position.",
                  {
                    style: {
                      borderRadius: "8px",
                      background: "#fffbe6",
                      color: "#081F2D",
                      fontWeight: "bold",
                    },
                  }
                );
              }
            }
          },
          (error) => {
            setUserPosition(DEFAULT_SYDNEY_COORDS);
            setHasUserInteracted(false);
            // Only show warning once to avoid spam
            if (!hasShownLocationWarning) {
              setHasShownLocationWarning(true);
              localStorage.setItem("hasShownLocationWarning", "true");
              toast("Location access denied. Using default Sydney position.", {
                style: {
                  borderRadius: "8px",
                  background: "#fffbe6",
                  color: "#081F2D",
                  fontWeight: "bold",
                },
              });
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );

        locationWatchId.current = navigator.geolocation.watchPosition(
          (position) => {
            const { longitude, latitude } = position.coords;

            if (isValidSydneyCoordinate(longitude, latitude)) {
              setUserPosition([longitude, latitude]);
            } else {
            }
          },
          (error) => {
            setUserPosition(DEFAULT_SYDNEY_COORDS);
            setHasUserInteracted(false);
            // Only show warning once to avoid spam
            if (!hasShownLocationWarning) {
              setHasShownLocationWarning(true);
              localStorage.setItem("hasShownLocationWarning", "true");
              toast("Location tracking error. Using default Sydney position.", {
                style: {
                  borderRadius: "8px",
                  background: "#fffbe6",
                  color: "#081F2D",
                  fontWeight: "bold",
                },
              });
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      } else {
      }
    } else {
      if (locationWatchId.current) {
        navigator.geolocation.clearWatch(locationWatchId.current);
        locationWatchId.current = null;
      }
      setUserPosition(DEFAULT_SYDNEY_COORDS);
      setHasUserInteracted(false);
    }

    return () => {
      if (locationWatchId.current) {
        navigator.geolocation.clearWatch(locationWatchId.current);
        locationWatchId.current = null;
      }
    };
  }, [selectedTreasure]);

  useEffect(() => {
    const loadTreasureData = async () => {
      try {
        const treasureDataResult = await getTreasureData();
        setCurrentTreasureData(treasureDataResult);
      } catch (error) {
        setCurrentTreasureData([]);
      }
    };

    loadTreasureData();

    const refreshInterval = setInterval(() => {
      loadTreasureData();
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (
      userData &&
      userData.collectedTreasures &&
      currentTreasureData.length > 0
    ) {
      const initialCenter = [151.2093, -33.8688];
      const allTreasures = currentTreasureData
        .map((treasure, index) => ({
          id: treasure.id,
          position: [
            treasure.coordinates.longitude,
            treasure.coordinates.latitude,
          ],
          found: false,
          title: treasure.name,
          description: treasure.offer,
          hint: treasure.hint,
          address: treasure.address,
          openingHours: treasure.hours,
          uniqueRedemption: treasure.code,
          image: treasure.image,
          distance: calculateDistance(
            initialCenter[1], // lat
            initialCenter[0], // lng
            treasure.coordinates.latitude,
            treasure.coordinates.longitude
          ),
        }))
        .sort((a, b) => a.distance - b.distance)
        .filter((treasure) => {
          const treasureId = normalizeTreasureId(treasure.id);
          return !userData.collectedTreasures.some(
            (collectedId) => normalizeTreasureId(collectedId) === treasureId
          );
        })
        .slice(0, 10);

      setTreasures(allTreasures);
    }
  }, [userData, currentTreasureData]);

  useEffect(() => {
    if (!permissionsChecked && !permissionInitRef.current) {
      permissionInitRef.current = true;
      checkAndInitializePermissions();
    }
  }, []);

  useEffect(() => {
    if (location.state && location.state.selectedGame) {
      setSelectedGame(location.state.selectedGame);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      if (debouncedMapMove.current) {
        clearTimeout(debouncedMapMove.current);
      }
    };
  }, []);

  useEffect(() => {
    setShowHintBalloon(true);
    const timer = setTimeout(() => {
      setShowHintBalloon(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const checkAndInitializePermissions = async () => {
    try {
      const cameraPermission = await navigator.permissions.query({
        name: "camera",
      });

      if (cameraPermission.state === "granted") {
        setPermissionsChecked(true);
      } else {
        setPermissionsChecked(true);
      }
    } catch (error) {
      setPermissionsChecked(true);
    }
  };

  useEffect(() => {
    let isProcessing = false;
    let lastProcessedTarget = null;
    let lastProcessedTime = 0;

    const handleMessage = (event) => {
      if (event.origin !== "https://nxtinteractive.8thwall.app") {
        return;
      }

      if (event.data && event.data.type === "imageTargetDetected") {
        const { targetName } = event.data;
        const currentTime = Date.now();

        if (isProcessing) {
          return;
        }

        if (
          lastProcessedTarget === targetName &&
          currentTime - lastProcessedTime < 10000
        ) {
          return;
        }

        isProcessing = true;
        lastProcessedTarget = targetName;
        lastProcessedTime = currentTime;

        const treasure = findTreasureByName(currentTreasureData, targetName);

        // alert(`treasure: ${targetName}`);

        if (treasure) {
          setRewardData(treasure);

          setTimeout(() => {
            setIsRewardPopupOpen(true);
            setTimeout(() => {
              isProcessing = false;
            }, 3000);
          }, 5000);
        } else {
          toast("Treasure not found", {
            style: {
              borderRadius: "8px",
              background: "#fffbe6",
              color: "#081F2D",
              fontWeight: "bold",
            },
          });
          isProcessing = false;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [currentTreasureData]);

  const handleBoostPlay = () => {
    setShowBoostPopup(false);
  };

  const handleTreasureClick = (treasure) => {
    // Use the full treasure data if available, otherwise find it by ID
    const fullTreasure =
      treasure.treasureData ||
      treasures.find(
        (t) => t.id === parseInt(treasure.id.replace("treasure-", ""))
      );

    // If clicking the same treasure, deselect it
    if (selectedTreasure?.id === fullTreasure?.id) {
      setSelectedTreasure(null);
    } else {
      // Select new treasure (don't reset location warning state)
      setSelectedTreasure(fullTreasure);
    }

    // hide reward popup when treasure is clicked
    setIsRewardPopupOpen(false);
  };

  const toggleView = (view) => {
    if (view !== activeView) {
      setActiveView(view);
      if (view === "camera") {
        setTimeout(() => {
          toast(
            "Spot the poster, scan it, and watch the treasure come to life!",
            {
              duration: 3000,
              position: "bottom-center",
              style: {
                background: "#081F2D",
                color: "#fff",
                borderRadius: "16px",
                padding: "16px",
                maxWidth: "90%",
                textAlign: "center",
                marginBottom: "120px", // Add margin to avoid overlap with bottom buttons
              },
            }
          );
        }, 1000);
      } else if (view === "map") {
        // Refresh location when switching back to map view
        if (navigator.geolocation) {
          setIsLocating(true);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { longitude, latitude } = position.coords;
              if (isValidSydneyCoordinate(longitude, latitude)) {
                setUserPosition([longitude, latitude]);
                setCenter([longitude, latitude]); // Center map on user's current location
                console.log(
                  "📍 [VIEW SWITCH] Location refreshed when switching to map view:",
                  [longitude, latitude]
                );
              }
              setIsLocating(false);
            },
            (error) => {
              console.log(
                "📍 [VIEW SWITCH] Location refresh failed when switching to map view:",
                error.message
              );
              setIsLocating(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0, // Don't use cached position
            }
          );
        }
      }
    }
  };

  const handleHintClick = () => {
    if (selectedTreasure) {
      setIsHintModalOpen(true);
    }
  };

  // Find nearby treasures based on current map center
  const findNearbyTreasures = (mapCenter) => {
    if (!currentTreasureData || currentTreasureData.length === 0) {
      return [];
    }

    const nearbyTreasures = currentTreasureData
      .map((treasure, index) => ({
        id: treasure.id,
        position: [
          treasure.coordinates.longitude,
          treasure.coordinates.latitude,
        ],
        found: false,
        title: treasure.name,
        description: treasure.offer,
        hint: treasure.hint,
        address: treasure.address,
        openingHours: treasure.hours,
        uniqueRedemption: treasure.code,
        image: treasure.image,
        distance: calculateDistance(
          mapCenter[1], // lat
          mapCenter[0], // lng
          treasure.coordinates.latitude,
          treasure.coordinates.longitude
        ),
      }))
      .sort((a, b) => a.distance - b.distance) // Sort by distance
      .filter((treasure) => {
        // Normalize treasure ID and check against collected treasures
        const treasureId = normalizeTreasureId(treasure.id);
        const isCollected = userData?.collectedTreasures?.some(
          (collectedId) => normalizeTreasureId(collectedId) === treasureId
        );

        return !isCollected;
      }) // Filter out collected
      .slice(0, 10); // Take the 10 closest uncollected

    return nearbyTreasures;
  };

  // Debounced map movement handler
  const debouncedMapMove = useRef(null);

  // Handle map movement with delay
  const handleMapMove = useCallback(
    (newCenter) => {
      setCenter(newCenter);

      // Mark that user has manually interacted with the map
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
      }

      // Clear existing timeout
      if (debouncedMapMove.current) {
        clearTimeout(debouncedMapMove.current);
      }

      // Set new timeout for finding nearby treasures
      debouncedMapMove.current = setTimeout(() => {
        // Only update treasures if userData and treasure data are available
        if (userData && currentTreasureData.length > 0) {
          const nearbyTreasures = findNearbyTreasures(newCenter);
          setTreasures(nearbyTreasures);
        }
      }, 500); // 500ms delay
    },
    [userData, currentTreasureData, hasUserInteracted]
  );

  // Prepare markers for Mapbox
  const mapMarkers = [
    // User position marker
    {
      id: "user",
      position: userPosition,
      title: "You are here",
      className: "current-location-marker",
      content: (
        <div className="current-location-marker">
          <div className="current-location-dot"></div>
          <div className="current-location-pulse"></div>
        </div>
      ),
    },
    // Treasure markers
    ...treasures.map((treasure) => ({
      id: `treasure-${treasure.id}`,
      position: treasure.position,
      title: treasure.title,
      description: treasure.description,
      className:
        selectedTreasure?.id === treasure.id
          ? "treasure-marker active"
          : "treasure-marker",
      icon: treasureImage,
      treasureData: treasure, // Include the full treasure data
    })),
  ];

  return (
    <div className="home">
      <HintModal
        isOpen={isHintModalOpen}
        onClose={() => setIsHintModalOpen(false)}
        hint={selectedTreasure?.hint}
      />

      <RewardPopup
        isOpen={isRewardPopupOpen}
        onClose={() => setIsRewardPopupOpen(false)}
        data={rewardData}
        isSavingTreasure={isSavingTreasure}
        onCollect={() => {
          // Set saving state to keep popup open
          setIsSavingTreasure(true);

          // Save the collected treasure ID to user data
          if (rewardData && rewardData.id) {
            const currentCollectedTreasures = userData.collectedTreasures || [];

            // Normalize treasure ID to ensure consistency
            const treasureId = normalizeTreasureId(rewardData.id);

            // Check if already collected
            if (currentCollectedTreasures.includes(treasureId)) {
              setIsSavingTreasure(false);
              setSelectedTreasure(null);
              setIsRewardPopupOpen(false);
              toast("Treasure already collected!", {
                style: {
                  borderRadius: "8px",
                  background: "#fffbe6",
                  color: "#081F2D",
                  fontWeight: "bold",
                },
              });
            } else {
              const updatedCollectedTreasures = [
                ...currentCollectedTreasures,
                treasureId,
              ];

              const updatedUserData = {
                ...userData,
                collectedTreasures: updatedCollectedTreasures,
              };

              updateUserData(updatedUserData);

              // Keep popup open for a moment to show saving
              setTimeout(() => {
                setIsSavingTreasure(false);
                setSelectedTreasure(null);
                setIsRewardPopupOpen(false);
              }, 1000);
            }
          } else {
            setIsSavingTreasure(false);
            setSelectedTreasure(null);
            setIsRewardPopupOpen(false);
          }
        }}
      />

      <BoostScorePopup
        isOpen={showBoostPopup}
        onClose={() => setShowBoostPopup(false)}
        onPlay={handleBoostPlay}
      />

      <GamePopup
        isOpen={selectedGame === "jigsaw"}
        onClose={() => setSelectedGame(null)}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h3 style={{ marginBottom: "10px", color: "#081F2D" }}>
            Complete the puzzle!
          </h3>
          <p style={{ fontSize: 12, fontWeight: "normal", color: "#081F2D" }}>
            Drag and drop to complete the puzzle
          </p>
        </div>
        <JigsawTrayPuzzle onClose={() => setSelectedGame(null)} />
      </GamePopup>

      <GamePopup
        isOpen={selectedGame === "flip"}
        onClose={() => setSelectedGame(null)}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h3 style={{ marginBottom: "10px", color: "#081F2D" }}>
            Match in the given time!
          </h3>
          <p style={{ fontSize: 12, fontWeight: "normal", color: "#081F2D" }}>
            Tap on the individual squares to flip the <br />
            cards, once a match they will stay visible.
          </p>
        </div>
        <FlipCardsGame onClose={() => setSelectedGame(null)} />
      </GamePopup>

      <GamePopup
        isOpen={selectedGame === "quiz"}
        onClose={() => setSelectedGame(null)}
      >
        <MarathonQuizGame onClose={() => setSelectedGame(null)} />
      </GamePopup>

      <Header />

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item pill">
          <i className="fas fa-gift"></i>
          <span>{userData?.collectedTreasures?.length || 0}</span>
        </div>
        <div className="stat-item pill">
          <i className="fas fa-coins"></i>
          <span>
            {(userData?.collectedTreasures?.length || 0) * 10 +
              (userData?.totalBoosterScore || 0)}{" "}
            Pts
          </span>
        </div>
      </div>

      {/* {activeView === "map" && (
        <MapModeToggle isLive={isLiveMap} onToggle={toggleMapMode} />
      )} */}

      <div className="views-container">
        <div
          className={`map-container ${activeView === "map" ? "active" : ""}`}
        >
          {/* Location Loading Indicator */}
          {/* {isLocating && (
            <div className="location-loading">
              <div className="loading-spinner"></div>
              <span>Detecting your location...</span>
            </div>
          )} */}

          {/* Floating Reset Button - only show when user has manually interacted */}
          {hasUserInteracted && userPosition && (
            <button
              className="map-reset-button"
              onClick={resetMapView}
              title="Reset map view to your location"
            >
              <i className="fas fa-crosshairs"></i>
            </button>
          )}

          <MapboxMap
            center={center}
            zoom={15}
            markers={mapMarkers}
            onMarkerClick={handleTreasureClick}
            onMapMove={handleMapMove}
            showRoute={
              !!selectedTreasure &&
              isValidSydneyCoordinate(userPosition[0], userPosition[1])
            }
            routeStart={
              selectedTreasure &&
              isValidSydneyCoordinate(userPosition[0], userPosition[1])
                ? userPosition
                : null
            }
            routeEnd={
              selectedTreasure &&
              isValidSydneyCoordinate(userPosition[0], userPosition[1])
                ? selectedTreasure.position
                : null
            }
          />
        </div>

        {/* Camera View */}
        {activeView === "camera" && (
          <div className="camera-container active">
            {/* Dynamic iframe URL based on user location */}
            <iframe
              src={getIframeUrl()}
              className="camera-webview"
              allow="camera; microphone; autoplay; encrypted-media; fullscreen; gyroscope; accelerometer; magnetometer"
              allowfullscreen
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              style={{
                visibility:
                  isHintModalOpen || isDrawerOpen ? "hidden" : "visible",
              }}
            />
          </div>
        )}
      </div>

      {/* Bottom Action Buttons */}
      <div className="bottom-actions">
        <div className="action-slider">
          <div className={`slider-pill ${activeView}`}></div>
          <button
            className={`action-button ${activeView === "map" ? "active" : ""}`}
            onClick={() => toggleView("map")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="3" fill="currentColor" />
              <path
                d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"
                fill="currentColor"
              />
            </svg>
          </button>
          <button
            className={`action-button ${
              activeView === "camera" ? "active" : ""
            }`}
            onClick={() => toggleView("camera")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 15.2C13.7673 15.2 15.2 13.7673 15.2 12C15.2 10.2327 13.7673 8.8 12 8.8C10.2327 8.8 8.8 10.2327 8.8 12C8.8 13.7673 10.2327 15.2 12 15.2Z"
                fill="currentColor"
              />
              <path
                d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4H16.83L15 2H9ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        {selectedTreasure && (
          <div className="hint-button-container">
            {!isValidSydneyCoordinate(userPosition[0], userPosition[1]) && (
              <div
                className="location-warning"
                style={{
                  position: "absolute",
                  top: "-40px",
                  right: "0",
                  background: "#fff3cd",
                  color: "#856404",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  border: "1px solid #ffeaa7",
                  zIndex: 1000,
                }}
              >
                ⚠️ Location not available
              </div>
            )}
            <button
              className="action-button clear-button"
              onClick={() => setSelectedTreasure(null)}
              title="Clear selection"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <button
              className="action-button idea-button"
              onClick={handleHintClick}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0 2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"
                  fill="currentColor"
                />
              </svg>
            </button>
            {showHintBalloon && (
              <div className="hint-balloon">
                <div className="hint-balloon-content">
                  Need help? Use a hint to uncover your
                  <br />
                  next hidden treasure.
                </div>
                <div className="hint-balloon-arrow"></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
